import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSession } from "@clerk/clerk-react";
import {
  Plus,
  Trash,
  MapPin,
  MagnifyingGlass,
  FloppyDisk,
  X,
  ArrowLeft,
  MapTrifold,
  FunnelSimple,
  House,
} from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { ClusterMap } from './components/ClusterMap';
import { ClusterType, UnitInCluster, UnitFilterCriteria } from 'shared/types/cluster';
import { ClusterTypeToggle } from './clusters/components/ClusterTypeToggle';
import { UnitFilterBuilder } from './clusters/components/filterBuilder/UnitFilterBuilder';
import { CLUSTER_UNITS_COLUMNS, CLUSTER_UNITS_SORT } from './clusters/components/ClusterUnitsTableConfig';
import { IconPicker, getIconByValue } from '@/components/dueDiligence/IconPicker';
import { UniversalTable, CellConfig } from '@/shared/components/UniversalTable';

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";

interface Cluster {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  type: ClusterType;
  unitFilterCriteria?: UnitFilterCriteria | null;
  units: UnitInCluster[];
  createdAt: string;
  updatedAt: string;
}

interface BuildingSummary {
  buildingId: string;
  buildingAddress: string;
  matchingUnitCount: number;
  totalUnitCount: number;
}

interface UnassignedUnit {
  id: string;
  boxNumber?: string;
  floor?: number;
  unitType?: string;
  building?: {
    id: string;
    googlePlaceLongAddress?: string;
    company?: {
      id: number;
      name: string;
    };
  };
}

export default function ClusterDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const [cluster, setCluster] = useState<Cluster | null>(null);
  const [clusterName, setClusterName] = useState('');
  const [clusterDescription, setClusterDescription] = useState('');
  const [clusterIcon, setClusterIcon] = useState('');
  const [clusterType, setClusterType] = useState<ClusterType>(ClusterType.MANUAL);
  const [units, setUnits] = useState<UnitInCluster[]>([]);
  const [buildingSummary, setBuildingSummary] = useState<BuildingSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [availableUnits, setAvailableUnits] = useState<UnassignedUnit[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isManagingUnits, setIsManagingUnits] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [unitSearchValue, setUnitSearchValue] = useState('');

  const isNewCluster = id === 'new';
  const isFilterCluster = clusterType === ClusterType.FILTER;

  useEffect(() => {
    if (!id) return;

    if (!isNewCluster) {
      fetchClusterData();
    } else {
      setIsLoading(false);
    }
  }, [id, session]);

  // Fetch units when cluster is loaded
  useEffect(() => {
    if (!isNewCluster && cluster?.id) {
      fetchClusterUnits();
    }
  }, [cluster?.id, cluster?.type]);

  const fetchClusterData = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_URL}/clusters/${id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch cluster: ${response.statusText}`);
      }

      const data = await response.json();
      setCluster(data);
      setClusterName(data.name || '');
      setClusterDescription(data.description || '');
      setClusterIcon(data.icon || '');
      setClusterType(data.type || ClusterType.MANUAL);
    } catch (error) {
      console.error('Error fetching cluster:', error);
      toast.error('Kon cluster niet laden');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClusterUnits = async () => {
    if (!cluster?.id) return;
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/${cluster.id}/units/grouped`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (response.ok) {
        const data = await response.json();
        setUnits(data.units || []);
        setBuildingSummary(data.buildingSummary || []);
      }
    } catch (error) {
      console.error('Error fetching cluster units:', error);
    }
  };

  const fetchAvailableUnits = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/unassigned-units`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (response.ok) {
        const data = await response.json();
        setAvailableUnits(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching available units:', error);
      setAvailableUnits([]);
    }
  };

  const filteredAvailableUnits = useMemo(() => {
    if (!searchTerm) return availableUnits;
    const query = searchTerm.toLowerCase();
    return availableUnits.filter(unit =>
      (unit.id && unit.id.toLowerCase().includes(query)) ||
      (unit.boxNumber && unit.boxNumber.toLowerCase().includes(query)) ||
      (unit.building?.id && unit.building.id.toLowerCase().includes(query)) ||
      (unit.building?.googlePlaceLongAddress && unit.building.googlePlaceLongAddress.toLowerCase().includes(query))
    );
  }, [searchTerm, availableUnits]);

  // Filter units by search value (client-side)
  const filteredUnits = useMemo(() => {
    if (!unitSearchValue.trim()) return units;
    const query = unitSearchValue.trim().toLowerCase();
    return units.filter((unit) => {
      const searchParts = [
        unit.id,
        unit.boxNumber,
        unit.referencePlus,
        unit.unitType,
        unit.unitSubtype,
        unit.floor != null ? String(unit.floor) : '',
        unit.grossSurface != null ? String(unit.grossSurface) : '',
        unit.netSurface != null ? String(unit.netSurface) : '',
        unit.building?.googlePlaceLongAddress,
        unit.building?.company?.name,
      ];
      return searchParts.some(
        (part) => part && part.toLowerCase().includes(query),
      );
    });
  }, [units, unitSearchValue]);

  // Derive buildings for map from units
  const buildingsForMap = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ id: string; googlePlaceLongAddress?: string }> = [];
    for (const unit of units) {
      if (unit.building?.id && !seen.has(unit.building.id)) {
        seen.add(unit.building.id);
        result.push({ id: unit.building.id, googlePlaceLongAddress: unit.building.googlePlaceLongAddress });
      }
    }
    return result;
  }, [units]);

  const handleSave = async () => {
    if (!clusterName.trim()) {
      toast.error('Voer een naam in voor de cluster');
      return;
    }

    try {
      setIsSaving(true);

      let response;
      if (isNewCluster) {
        if (isFilterCluster) {
          const payload = {
            name: clusterName.trim(),
            description: clusterDescription.trim() || undefined,
            icon: clusterIcon || undefined,
            unitFilterCriteria: {
              version: '1.0',
              root: {
                id: crypto.randomUUID(),
                logicalOperator: 'and',
                conditions: [],
              },
            },
          };
          response = await fetchWithAuth(`${API_URL}/clusters/unit-filter`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }, session);
        } else {
          const payload = {
            name: clusterName.trim(),
            description: clusterDescription.trim() || undefined,
            icon: clusterIcon || undefined,
            type: ClusterType.MANUAL,
          };
          response = await fetchWithAuth(`${API_URL}/clusters`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }, session);
        }
      } else {
        const payload: Record<string, unknown> = {
          name: clusterName.trim(),
          description: clusterDescription.trim() || undefined,
          icon: clusterIcon || undefined,
        };

        response = await fetchWithAuth(`${API_URL}/clusters/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }, session);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save cluster: ${response.statusText}`);
      }

      const savedCluster = await response.json();
      toast.success(isNewCluster ? 'Cluster aangemaakt' : 'Cluster opgeslagen');

      if (isNewCluster) {
        navigate(`/clusters/${savedCluster.id}`);
      } else {
        await fetchClusterData();
        await fetchClusterUnits();
      }
    } catch (error) {
      console.error('Error saving cluster:', error);
      toast.error(error instanceof Error ? error.message : 'Kon cluster niet opslaan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNewCluster) return;
    try {
      setIsDeleting(true);
      const response = await fetchWithAuth(`${API_URL}/clusters/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to delete cluster: ${response.statusText}`);
      }

      toast.success('Cluster verwijderd');
      navigate('/clusters');
    } catch (error) {
      console.error('Error deleting cluster:', error);
      toast.error('Kon cluster niet verwijderen');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleAddUnit = async (unitId: string) => {
    if (isNewCluster) return;
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/${id}/units/${unitId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to add unit: ${response.statusText}`);
      }

      toast.success('Eenheid toegevoegd');
      await fetchClusterUnits();
      await fetchAvailableUnits();
    } catch (error) {
      console.error('Error adding unit:', error);
      toast.error('Kon eenheid niet toevoegen');
    }
  };

  const handleRemoveUnit = async (unitId: string) => {
    if (isNewCluster) return;
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/${id}/units/${unitId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to remove unit: ${response.statusText}`);
      }

      toast.success('Eenheid verwijderd uit cluster');
      await fetchClusterUnits();
    } catch (error) {
      console.error('Error removing unit:', error);
      toast.error('Kon eenheid niet verwijderen');
    }
  };

  const getMainCompany = () => {
    if (!units.length) return null;
    const companies = units
      .map(u => u.building?.company)
      .filter((company): company is { id: number; name: string } => !!company);

    if (companies.length === 0) return null;

    const companyCount = companies.reduce((acc, company) => {
      acc[company.name] = (acc[company.name] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mainCompany = Object.keys(companyCount).reduce((a, b) =>
      companyCount[a] > companyCount[b] ? a : b
    );
    return companies.find(c => c.name === mainCompany);
  };

  // Shared cell renderers for unit rows
  const renderUnitCells = useCallback((row: UnitInCluster): CellConfig[] => [
    {
      id: 'unitId',
      content: (
        <span
          className="font-medium text-foreground text-sm cursor-default truncate"
          title={row.building?.googlePlaceLongAddress || ''}
        >
          {row.boxNumber || row.id.slice(0, 8)}
        </span>
      ),
    },
    {
      id: 'referencePlus',
      content: (
        <span className="text-sm">{row.referencePlus || '—'}</span>
      ),
    },
    {
      id: 'unitType',
      content: (
        <div className="flex items-center gap-1 flex-wrap">
          {row.unitType ? (
            <Badge variant="secondary" className="text-xs">{row.unitType}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
          {row.unitSubtype && (
            <Badge variant="outline" className="text-xs">{row.unitSubtype}</Badge>
          )}
        </div>
      ),
    },
    {
      id: 'floor',
      content: row.floor != null ? (
        <span className="text-sm">{row.floor}</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    },
    {
      id: 'grossSurface',
      content: row.grossSurface != null ? (
        <span className="text-sm">{row.grossSurface}</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    },
    {
      id: 'netSurface',
      content: row.netSurface != null ? (
        <span className="text-sm">{row.netSurface}</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    },
    {
      id: 'building',
      content: (
        <span className="text-sm truncate">
          {row.building?.googlePlaceLongAddress || '—'}
        </span>
      ),
    },
    {
      id: 'company',
      content: row.building?.company ? (
        <Badge variant="outline" className="text-xs">{row.building.company.name}</Badge>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    },
  ], []);

  // Render row for manual cluster units (with remove action)
  const renderManualUnitRow = useCallback((row: UnitInCluster): CellConfig[] => {
    return [
      ...renderUnitCells(row),
      {
        id: 'actions',
        content: (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveUnit(row.id);
            }}
            className="text-muted-foreground hover:text-destructive h-7 w-7"
          >
            <X className="size-4" />
          </Button>
        ),
        align: 'center' as const,
      },
    ];
  }, [renderUnitCells, handleRemoveUnit]);

  // Render row for filter cluster units (read-only, no actions)
  const renderFilterUnitRow = useCallback((row: UnitInCluster): CellConfig[] => {
    return renderUnitCells(row);
  }, [renderUnitCells]);

  // Column config for filter table (without actions column)
  const filterColumnsConfig = useMemo(() => ({
    ...CLUSTER_UNITS_COLUMNS,
    columns: CLUSTER_UNITS_COLUMNS.columns.filter(c => c.id !== 'actions'),
    localStorageKey: 'cluster-filter-units-table-v3',
  }), []);

  // Header icon
  const HeaderIcon = clusterIcon ? getIconByValue(clusterIcon) : null;

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-3">
        <Spinner className="size-8 text-brick-primary" />
        <p className="text-muted-foreground text-sm">Cluster laden...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/clusters')}
            className="mt-1"
          >
            <ArrowLeft className="size-5" />
          </Button>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              {HeaderIcon ? (
                <HeaderIcon className="size-6 text-brick-primary" weight="duotone" />
              ) : (
                <MapTrifold className="size-6 text-muted-foreground" weight="duotone" />
              )}
              <h1 className="text-2xl font-semibold text-foreground">
                {isNewCluster ? 'Nieuwe Cluster' : cluster?.name || 'Cluster Details'}
              </h1>
            </div>
            <p className="text-muted-foreground">
              {isNewCluster ? 'Maak een nieuwe cluster aan om eenheden te groeperen' : 'Beheer eenheden in deze cluster'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={isSaving || !clusterName.trim()}
            className="bg-brick-primary hover:bg-brick-primary/90 text-white"
          >
            {isSaving && <Spinner className="size-4" />}
            <FloppyDisk className="size-4" />
            Opslaan
          </Button>
          {!isNewCluster && (
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(true)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash className="size-4" />
              Verwijderen
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Cluster Info & Units */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapTrifold className="size-5 text-brick-primary" weight="duotone" />
                Algemene informatie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="cluster-name" className="text-sm font-medium text-foreground">
                  Cluster naam
                </label>
                <Input
                  id="cluster-name"
                  placeholder="Bijv. Regio Noord of Portfolio A"
                  value={clusterName}
                  onChange={(e) => setClusterName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="cluster-description" className="text-sm font-medium text-foreground">
                  Beschrijving
                </label>
                <Textarea
                  id="cluster-description"
                  placeholder="Optionele beschrijving van de cluster..."
                  value={clusterDescription}
                  onChange={(e) => setClusterDescription(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Icon Picker */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Icoon
                </label>
                <IconPicker
                  value={clusterIcon}
                  onChange={setClusterIcon}
                  defaultCategory="real_estate"
                />
              </div>

              {/* Cluster Type Toggle - only show for new clusters */}
              {isNewCluster && (
                <>
                  <Separator />
                  <ClusterTypeToggle
                    value={clusterType}
                    onChange={setClusterType}
                  />
                </>
              )}

              {/* Show cluster type badge for existing clusters */}
              {!isNewCluster && (
                <div className="flex items-center gap-2 pt-2">
                  <span className="text-sm text-muted-foreground">Type:</span>
                  <Badge variant={isFilterCluster ? 'default' : 'secondary'} className={isFilterCluster ? 'bg-moss-primary' : ''}>
                    {isFilterCluster ? (
                      <><FunnelSimple className="size-3 mr-1" /> Filter-gebaseerd</>
                    ) : (
                      <><MapTrifold className="size-3 mr-1" /> Handmatig</>
                    )}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Units Section - Manual Clusters */}
          {!isNewCluster && !isFilterCluster && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <House className="size-5 text-brick-primary" weight="duotone" />
                    Eenheden
                  </CardTitle>
                  <CardDescription>
                    {units.length} eenhe{units.length === 1 ? 'id' : 'den'} in {buildingSummary.length} gebouw{buildingSummary.length !== 1 ? 'en' : ''}
                  </CardDescription>
                </div>
                <Button
                  onClick={() => { setIsManagingUnits(true); fetchAvailableUnits(); }}
                  variant="outline"
                  size="sm"
                >
                  <Plus className="size-4" />
                  Eenheid toevoegen
                </Button>
              </CardHeader>
              <CardContent>
                {units.length > 0 ? (
                  <UniversalTable
                    rows={filteredUnits}
                    title=""
                    columnsConfig={CLUSTER_UNITS_COLUMNS}
                    sortConfig={CLUSTER_UNITS_SORT}
                    renderRow={renderManualUnitRow}
                    getRowKey={(row) => row.id}
                    searchConfig={{
                      searchPlaceholder: 'Zoek eenheden...',
                      searchValue: unitSearchValue,
                      onSearchChange: setUnitSearchValue,
                      showSpacer: false,
                      serverSideFiltering: true,
                    }}
                    onRowClick={(row) => {
                      if (row.building?.id) {
                        navigate(`/building/${row.building.id}`);
                      }
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 rounded-lg border-2 border-dashed border-border/50">
                    <House className="size-10 text-muted-foreground/50 mb-3" weight="duotone" />
                    <p className="text-muted-foreground text-sm mb-4">
                      Nog geen eenheden in deze cluster
                    </p>
                    <Button
                      onClick={() => { setIsManagingUnits(true); fetchAvailableUnits(); }}
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="size-4" />
                      Eenheid toevoegen
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Filter Builder - for filter-based clusters */}
          {isFilterCluster && !isNewCluster && cluster && (
            <UnitFilterBuilder
              clusterId={cluster.id}
              initialCriteria={cluster.unitFilterCriteria}
              onSaved={() => {
                fetchClusterData();
                fetchClusterUnits();
              }}
            />
          )}

          {/* Units Section - Filter-based Clusters (read-only) */}
          {!isNewCluster && isFilterCluster && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <House className="size-5 text-moss-primary" weight="duotone" />
                    Overeenkomende eenheden
                  </CardTitle>
                  <CardDescription>
                    {units.length} eenhe{units.length === 1 ? 'id' : 'den'} voldoen aan de filter criteria
                  </CardDescription>
                </div>
                <Badge variant="secondary" className="bg-moss-secondary-light text-moss-primary">
                  <FunnelSimple className="size-3 mr-1" />
                  Automatisch
                </Badge>
              </CardHeader>
              <CardContent>
                {units.length > 0 ? (
                  <UniversalTable
                    rows={filteredUnits}
                    title=""
                    columnsConfig={filterColumnsConfig}
                    sortConfig={CLUSTER_UNITS_SORT}
                    renderRow={renderFilterUnitRow}
                    getRowKey={(row) => row.id}
                    searchConfig={{
                      searchPlaceholder: 'Zoek eenheden...',
                      searchValue: unitSearchValue,
                      onSearchChange: setUnitSearchValue,
                      showSpacer: false,
                      serverSideFiltering: true,
                    }}
                    onRowClick={(row) => {
                      if (row.building?.id) {
                        navigate(`/building/${row.building.id}`);
                      }
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 rounded-lg border-2 border-dashed border-border/50">
                    <FunnelSimple className="size-10 text-muted-foreground/50 mb-3" weight="duotone" />
                    <p className="text-muted-foreground text-sm">
                      Geen eenheden voldoen aan de huidige filter criteria
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Map & Overview */}
        <div className="space-y-6">
          {/* Google Map */}
          {!isNewCluster && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapTrifold className="size-4 text-brick-primary" weight="duotone" />
                  Locatie overzicht
                </CardTitle>
                <CardDescription>
                  Alle gebouwen in deze cluster op de kaart
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ClusterMap
                  buildings={buildingsForMap}
                  height="350px"
                  interactive={true}
                  showPlaceholder={true}
                />
              </CardContent>
            </Card>
          )}

          {/* Cluster Overview */}
          {!isNewCluster && cluster && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Overzicht</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className={`text-2xl font-bold ${isFilterCluster ? 'text-moss-primary' : 'text-brick-primary'}`}>
                      {units.length}
                    </div>
                    <div className="text-xs text-muted-foreground">Eenheden</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className={`text-2xl font-bold ${isFilterCluster ? 'text-moss-primary' : 'text-brick-primary'}`}>
                      {buildingSummary.length}
                    </div>
                    <div className="text-xs text-muted-foreground">Gebouwen</div>
                  </div>
                </div>

                <Separator />

                {/* Company Info */}
                {!isFilterCluster && getMainCompany() && (
                  <>
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-2">Hoofdbedrijf</h4>
                      <Badge variant="secondary">
                        {getMainCompany()?.name}
                      </Badge>
                    </div>
                    <Separator />
                  </>
                )}

                {/* Dates */}
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>
                    Aangemaakt: {new Date(cluster.createdAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                  <p>
                    Bijgewerkt: {new Date(cluster.updatedAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Unit Management Dialog */}
      <Dialog open={isManagingUnits} onOpenChange={setIsManagingUnits}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Eenheid toevoegen aan cluster</DialogTitle>
            <DialogDescription>
              Selecteer een eenheid om toe te voegen. Alleen niet-toegewezen eenheden worden getoond.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Zoek op ID, boxnummer, adres..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="max-h-96 overflow-y-auto space-y-2">
              {filteredAvailableUnits.length > 0 ? (
                filteredAvailableUnits.map((unit) => (
                  <div
                    key={unit.id}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground text-sm">
                        {unit.boxNumber || unit.id}
                      </h4>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                        <MapPin className="size-3 flex-shrink-0" />
                        {unit.building?.googlePlaceLongAddress || 'Geen adres'}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {unit.building?.company && (
                          <Badge variant="secondary" className="text-xs">
                            {unit.building.company.name}
                          </Badge>
                        )}
                        {unit.unitType && (
                          <Badge variant="outline" className="text-xs">
                            {unit.unitType}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddUnit(unit.id)}
                      className="bg-brick-primary hover:bg-brick-primary/90 text-white"
                    >
                      Toevoegen
                    </Button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {searchTerm ? 'Geen eenheden gevonden' : 'Geen beschikbare eenheden'}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManagingUnits(false)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cluster verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je de cluster "{cluster?.name}" wilt verwijderen?
              {units.length > 0 && (
                <span className="block mt-2 font-medium text-foreground">
                  De {units.length} eenheden in deze cluster worden niet verwijderd,
                  maar zijn daarna niet meer aan deze cluster gekoppeld.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting && <Spinner className="size-4" />}
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
